/**
 * The `poqpoq` namespace in glTF `extras` — ONE door for every declaration
 * this suite writes onto a node (ADR-030 §14 introduced it for the head
 * seating; arm-space is the second tenant). Plain `extras`, spec-sanctioned
 * and reader-free: three carries `Object3D.userData` ↔ `extras` in both
 * directions, so a declaration written on a node rides the file without any
 * exporter or loader plugin. The discipline (the trajectory memo): the file
 * must be correct WITHOUT a reader and better WITH one — a declaration never
 * changes what a consumer that ignores it sees.
 *
 * Shape: `extras.poqpoq.<key> = <value>`. Other keys under `poqpoq`, and
 * every other userData key, survive a write; an emptied namespace is removed.
 *
 * Depends on: nothing. Depended on by: head-space (headSeating), arm-space
 * (armSpace), and their scene twins in the adapter.
 */

/** The glTF `extras` namespace this suite writes on nodes it has decided about. */
export const POQPOQ_EXTRAS_KEY = 'poqpoq';

/** The value under `extras.poqpoq.<key>`, or undefined when absent. `null` when the namespace is not an object. */
export function readPoqpoqExtra(userData: unknown, key: string): unknown {
  if (userData === null || typeof userData !== 'object') return undefined;
  const ns = (userData as Record<string, unknown>)[POQPOQ_EXTRAS_KEY];
  if (ns === undefined || ns === null) return undefined;
  if (typeof ns !== 'object') return null;
  const raw = (ns as Record<string, unknown>)[key];
  return raw === null ? undefined : raw;
}

/**
 * A node's userData with `extras.poqpoq.<key>` written (or, for undefined,
 * removed) — every other key preserved. Returns a NEW object; the caller assigns it.
 */
export function withPoqpoqExtra(userData: unknown, key: string, value: unknown): Record<string, unknown> {
  const base: Record<string, unknown> =
    userData !== null && typeof userData === 'object' ? { ...(userData as Record<string, unknown>) } : {};
  const prior = base[POQPOQ_EXTRAS_KEY];
  const ns: Record<string, unknown> = prior !== null && typeof prior === 'object' ? { ...(prior as Record<string, unknown>) } : {};
  if (value === undefined) delete ns[key];
  else ns[key] = value;
  if (Object.keys(ns).length === 0) delete base[POQPOQ_EXTRAS_KEY];
  else base[POQPOQ_EXTRAS_KEY] = ns;
  return base;
}
